import {
	Rocket,
	LayoutDashboard,
	Users,
	Briefcase,
	Sparkles,
	Building2,
	UserCog,
	ListChecks,
	Search,
	Calendar,
	CheckSquare,
	Kanban,
	BarChart3,
	Bell,
	Settings,
	ShieldCheck,
	Brain,
	Linkedin,
	Chrome,
	MessageSquare,
	CreditCard,
	Compass,
	LifeBuoy,
} from "lucide-react";
import type { HelpSection } from "./types";

const section = (
	id: string,
	icon: HelpSection["icon"],
	topicIds: { id: string; hasSteps?: boolean; hasTips?: boolean; action?: HelpSection["topics"][number]["action"] }[],
): HelpSection => ({
	id,
	icon,
	titleKey: `help.sections.${id}.title`,
	descriptionKey: `help.sections.${id}.description`,
	topics: topicIds.map(({ id: topicId, hasSteps = true, hasTips = false, action }) => ({
		id: topicId,
		titleKey: `help.sections.${id}.topics.${topicId}.title`,
		overviewKey: `help.sections.${id}.topics.${topicId}.overview`,
		stepsKey: hasSteps ? `help.sections.${id}.topics.${topicId}.steps` : undefined,
		tipsKey: hasTips ? `help.sections.${id}.topics.${topicId}.tips` : undefined,
		action,
	})),
});

export const helpSections: HelpSection[] = [
	section("getting-started", Rocket, [
		{ id: "sign-in", hasTips: true },
		{ id: "onboarding", hasTips: true },
		{ id: "workspace-layout", hasTips: true },
		{ id: "language-theme", hasTips: true },
		{ id: "restart-tour", action: { type: "restartTour", labelKey: "help.actions.restartTour" } },
	]),
	section("dashboard", LayoutDashboard, [
		{ id: "kpis", hasTips: true },
		{ id: "quick-actions", hasTips: true },
		{ id: "widgets", hasTips: true },
		{ id: "funnel-charts", hasTips: true },
		{ id: "export-pdf" },
	]),
	section("candidates", Users, [
		{ id: "browse-filter", hasTips: true },
		{ id: "add-manual", hasTips: true },
		{ id: "cv-upload", hasTips: true },
		{ id: "linkedin-import", hasTips: true },
		{ id: "bulk-import", hasTips: true },
		{ id: "profile-tabs", hasTips: true },
		{ id: "duplicates", hasTips: true },
		{ id: "favorites-tags", hasTips: true },
	]),
	section("jobs", Briefcase, [
		{ id: "create-manual", hasTips: true },
		{ id: "create-from-description", hasTips: true },
		{ id: "ai-analysis", hasTips: true },
		{ id: "top-matches", hasTips: true },
		{ id: "kanban-per-job" },
		{ id: "statuses" },
		{ id: "public-pages", hasTips: true },
		{ id: "team-collaboration" },
	]),
	section("matches", Sparkles, [
		{ id: "how-matching-works", hasTips: true },
		{ id: "score-threshold" },
		{ id: "match-detail" },
		{ id: "status-flow" },
		{ id: "interview-prep" },
		{ id: "bulk-actions" },
		{ id: "notifications-subscribe" },
		{ id: "pdf-report" },
	]),
	section("companies", Building2, [
		{ id: "add-company" },
		{ id: "contacts" },
		{ id: "positions-jobs" },
		{ id: "linked-entities" },
		{ id: "custom-fields" },
	]),
	section("workforce-bench", UserCog, [
		{ id: "assignments" },
		{ id: "rates", hasTips: true },
		{ id: "bench" },
		{ id: "transition-templates" },
		{ id: "extend-end" },
		{ id: "reports" },
	]),
	section("lists", ListChecks, [
		{ id: "create-list" },
		{ id: "manage-members" },
		{ id: "ai-list-search" },
		{ id: "custom-fields" },
		{ id: "history-export" },
	]),
	section("queries", Search, [
		{ id: "basic-search" },
		{ id: "query-builder", hasTips: true },
		{ id: "natural-language" },
		{ id: "saved-queries" },
		{ id: "export-csv" },
	]),
	section("calendar", Calendar, [
		{ id: "views" },
		{ id: "create-event" },
		{ id: "link-entities" },
		{ id: "edit-delete" },
	]),
	section("tasks", CheckSquare, [
		{ id: "create-task" },
		{ id: "statuses-priorities" },
		{ id: "filters" },
		{ id: "link-entities" },
		{ id: "comments" },
	]),
	section("kanban", Kanban, [
		{ id: "global-board" },
		{ id: "per-entity-boards" },
		{ id: "drag-drop" },
		{ id: "filters-colors" },
	]),
	section("analytics", BarChart3, [
		{ id: "kpi-dashboard" },
		{ id: "date-user-filters" },
		{ id: "funnel-conversion" },
		{ id: "leaderboard" },
		{ id: "workforce-kpis" },
		{ id: "export" },
	]),
	section("notifications", Bell, [
		{ id: "bell" },
		{ id: "notifications-page" },
		{ id: "types" },
		{ id: "preferences" },
		{ id: "entity-subscriptions" },
		{ id: "mark-read" },
	]),
	section("settings", Settings, [
		{ id: "general" },
		{ id: "notifications" },
		{ id: "email" },
		{ id: "integrations", hasTips: true },
		{ id: "document-upload" },
	]),
	section("admin", ShieldCheck, [
		{ id: "users-roles" },
		{ id: "billing" },
		{ id: "custom-fields" },
		{ id: "email-templates" },
		{ id: "cv-templates" },
		{ id: "public-jobs" },
		{ id: "transition-templates" },
	]),
	section("ai-features", Brain, [
		{ id: "job-analysis" },
		{ id: "candidate-analysis" },
		{ id: "matching", hasTips: true },
		{ id: "nl-queries" },
		{ id: "cv-generation" },
		{ id: "interview-questions" },
		{ id: "deduplication-clustering" },
	]),
	section("linkedin-unipile", Linkedin, [
		{ id: "connect-account" },
		{ id: "import-profiles" },
		{ id: "merge-update" },
		{ id: "limits-safety" },
	]),
	section("chrome-extension", Chrome, [
		{ id: "install" },
		{ id: "capture-profile" },
		{ id: "sync" },
		{ id: "auth-sync" },
		{ id: "troubleshooting" },
	]),
	section("collaboration", MessageSquare, [
		{ id: "comments" },
		{ id: "documents" },
		{ id: "mailbox" },
		{ id: "shared-access" },
	]),
	section("billing", CreditCard, [
		{ id: "plans-limits" },
		{ id: "stripe" },
		{ id: "grace-period" },
		{ id: "upgrade-downgrade" },
		{ id: "trial" },
	]),
	section("guided-tour", Compass, [
		{ id: "what-it-covers" },
		{ id: "when-it-runs" },
		{ id: "restart", action: { type: "restartTour", labelKey: "help.actions.restartTour" } },
	]),
	section("faq-troubleshooting", LifeBuoy, [
		{ id: "import-issues" },
		{ id: "integration-reconnect" },
		{ id: "missing-data" },
		{ id: "performance" },
		{ id: "contact-support", action: { type: "link", target: "mailto:support@digger.hr", labelKey: "help.actions.contactSupport" } },
	]),
];
